/**
 * Testimonial Carousel — frontend animation (view.js).
 *
 * Loaded only on pages that contain the block (declared as `viewScript` in
 * block.json). Handles all client-side behaviour: SplitText line-reveal
 * transitions, prev/next navigation, keyboard support, autoplay, and
 * ScrollTrigger-based pause/resume.
 *
 * GSAP dependencies (gsap, ScrollTrigger, SplitText) are loaded from the
 * jsDelivr CDN via inc/enqueue.php and accessed here as browser globals.
 * They are NOT imported as ES modules to avoid bundling a second copy
 * alongside the CDN version.
 *
 * @package Vertaview
 * @author  Little Dash <hello@littledash.com.au>
 */

/* global gsap, ScrollTrigger, SplitText */

( function () {
	'use strict';

	/**
	 * Detect the user's reduced-motion preference once at boot.
	 * If true, SplitText is skipped entirely and all transitions fall back to
	 * simple opacity cross-fades so no line movement occurs.
	 *
	 * @type {boolean}
	 */
	const REDUCED_MOTION = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	).matches;

	/** Seconds after quote lines begin revealing before name/subtitle animate in. */
	const META_REVEAL_DELAY = 0.2;

	/**
	 * Initialise a single carousel instance.
	 * Called once for each [data-testimonial-wrap] element found on the page,
	 * so multiple block instances are fully independent.
	 *
	 * @param {HTMLElement} wrap The outermost carousel element (the <section>).
	 */
	function initCarousel( wrap ) {
		// Collect all slide elements and the navigation controls from the DOM.
		const items     = [ ...wrap.querySelectorAll( '[data-testimonial-item]' ) ];
		const prevBtn   = wrap.querySelector( '[data-prev]' );
		const nextBtn   = wrap.querySelector( '[data-next]' );
		const counterEl = wrap.querySelector( '[data-current]' );

		// Read autoplay settings from data attributes written by render.php.
		// Using data attributes (rather than a JSON blob) keeps the PHP simple
		// and lets us read per-instance config without a shared global object.
		const autoplay         = wrap.dataset.autoplay === 'true';
		const autoplayDuration = parseInt( wrap.dataset.autoplayDuration, 10 ) || 5000;

		// Nothing to animate if there are no slides.
		if ( ! items.length ) return;

		// --- Mutable state ------------------------------------------------------

		let current    = 0;     // Index of the currently visible slide.
		let animating  = false; // Lock to prevent overlapping transitions.
		let autoTimer  = null;  // setTimeout handle for autoplay.
		let inViewport = false; // Tracks scroll visibility for keyboard + autoplay.

		// --- SplitText setup ----------------------------------------------------

		/**
		 * One entry per slide: either an array of SplitText instances (one per
		 * [data-testimonial-split] element) or null when the slide has no
		 * split-able text (or when reduced motion is active).
		 *
		 * Stored so we could revert splits on HMR or teardown if needed.
		 *
		 * @type {Array<Array<SplitText>|null>}
		 */
		const splits = [];

		/**
		 * Wrap every .tc-line within `el` in a .tc-line-outer div (overflow: hidden).
		 * This is the mask that hides the line when yPercent is ±100, producing
		 * the "wipe in from below" reveal effect. Called from onSplit() so the
		 * wrap is rebuilt every time SplitText re-splits (autoSplit re-splits
		 * lines whenever the element's width changes, e.g. on viewport resize
		 * or orientation change, keeping the split accurate to the current
		 * layout instead of the widths measured at page load).
		 *
		 * @param {HTMLElement} el A [data-testimonial-split] element.
		 */
		function wrapLines( el ) {
			el.querySelectorAll( '.tc-line' ).forEach( ( line ) => {
				const outer = document.createElement( 'div' );
				outer.className = 'tc-line-outer';
				line.parentNode.insertBefore( outer, line );
				outer.appendChild( line );
			} );
		}

		if ( ! REDUCED_MOTION && typeof SplitText !== 'undefined' ) {
			// Register with the GSAP core instance that was loaded from CDN.
			gsap.registerPlugin( SplitText );

			items.forEach( ( item ) => {
				// Each [data-testimonial-split] element (quote text, name, subtitle)
				// gets its own SplitText instance so lines are calculated correctly
				// per element rather than across the whole blockquote.
				const splitEls = [
					...item.querySelectorAll( '[data-testimonial-split]' ),
				];
				if ( ! splitEls.length ) {
					splits.push( null );
					return;
				}

				const itemSplits = splitEls.map(
					( el ) =>
						new SplitText( el, {
							type: 'lines',
							linesClass: 'tc-line', // CSS class applied to each wrapped line.
							aria: 'none', // Avoid aria-label on generic elements (div/span/cite).
							autoSplit: true, // Re-split on width change (resize) or web font load.
							onSplit() {
								wrapLines( el );
							},
						} )
				);
				splits.push( itemSplits );
			} );
		}

		// --- DOM helpers --------------------------------------------------------

		/**
		 * Return .tc-line elements for the quote body.
		 *
		 * @param {HTMLElement} item A slide element.
		 * @return {HTMLElement[]}
		 */
		function getQuoteLines( item ) {
			const text = item.querySelector( '.testimonial-carousel__text' );
			return text ? [ ...text.querySelectorAll( '.tc-line' ) ] : [];
		}

		/**
		 * Return .tc-line elements for the name and subtitle.
		 *
		 * @param {HTMLElement} item A slide element.
		 * @return {HTMLElement[]}
		 */
		function getMetaLines( item ) {
			return [
				...item.querySelectorAll(
					'.testimonial-carousel__name .tc-line, .testimonial-carousel__subtitle .tc-line'
				),
			];
		}

		/**
		 * Return all animated lines within a slide.
		 *
		 * @param {HTMLElement} item A slide element.
		 * @return {HTMLElement[]}
		 */
		function getLines( item ) {
			return [ ...getQuoteLines( item ), ...getMetaLines( item ) ];
		}

		/**
		 * Return the [data-testimonial-img] element within a slide, or null.
		 * The render.php only emits this element when the testimonial has a
		 * featured image, so null is a valid and expected result.
		 *
		 * @param {HTMLElement} item A slide element.
		 * @return {HTMLElement|null}
		 */
		function getImg( item ) {
			return item.querySelector( '[data-testimonial-img]' );
		}

		// --- Initial state ------------------------------------------------------

		// Slides are stacked in the same grid cell (see style.scss) so the list
		// height equals the tallest slide. Opacity + z-index swaps avoid
		// display:none toggles that collapse layout and cause jumpy transitions.
		items.forEach( ( item, i ) => {
			if ( i === 0 ) {
				gsap.set( item, { autoAlpha: 1, zIndex: 1 } );
				return;
			}
			gsap.set( item, { autoAlpha: 0, zIndex: 0 } );
		} );

		if ( ! REDUCED_MOTION ) {
			const firstQuoteLines = getQuoteLines( items[ 0 ] );
			const firstMetaLines  = getMetaLines( items[ 0 ] );

			if ( firstQuoteLines.length ) {
				gsap.set( firstQuoteLines, { yPercent: 100 } );
				gsap.to( firstQuoteLines, {
					yPercent: 0,
					duration: 0.8,
					stagger: 0.06,
					ease: 'power3.out',
					delay: 0.25,
				} );
			}

			if ( firstMetaLines.length ) {
				gsap.set( firstMetaLines, { yPercent: 100 } );
				gsap.to( firstMetaLines, {
					yPercent: 0,
					duration: 0.8,
					stagger: 0.06,
					ease: 'power3.out',
					delay: 0.25 + META_REVEAL_DELAY,
				} );
			}

			// Animate the first slide's image up from behind a clip-path mask.
			const firstImg = getImg( items[ 0 ] );
			if ( firstImg ) {
				gsap.set( firstImg, { clipPath: 'inset(100% 0 0 0)' } ); // Fully clipped.
				gsap.to( firstImg, {
					clipPath: 'inset(0% 0 0 0)', // Reveal downward.
					duration: 1,
					ease: 'power3.out',
					delay: 0.1,
				} );
			}
		} else {
			// Reduced motion: simple fade in with no movement.
			gsap.fromTo( items[ 0 ], { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4 } );
		}

		// Single slide — no navigation, autoplay, or keyboard controls.
		if ( items.length < 2 ) return;

		// --- Slide transition ---------------------------------------------------

		/**
		 * Animate from the current slide to the slide at index `next`.
		 *
		 * The `direction` parameter (+1 forward, -1 backward) determines which
		 * direction the lines exit/enter, giving a sense of spatial continuity.
		 *
		 * The transition is built as a GSAP timeline so all tweens share a single
		 * playhead; the onComplete callback runs once when every tween is done.
		 *
		 * @param {number} next      Target slide index.
		 * @param {number} direction +1 (forward) or -1 (backward).
		 */
		function goTo( next, direction ) {
			// Prevent queueing transitions while one is already running.
			if ( animating || next === current ) return;
			animating = true;

			const outItem  = items[ current ]; // Currently visible slide (exits).
			const inItem   = items[ next ];    // Target slide (enters).
			const outLines      = getLines( outItem );
			const inQuoteLines  = getQuoteLines( inItem );
			const inMetaLines   = getMetaLines( inItem );
			const outImg   = getImg( outItem );
			const inImg    = getImg( inItem );

			if ( counterEl ) counterEl.textContent = String( next + 1 );

			const tl = gsap.timeline( {
				onComplete() {
					outItem.setAttribute( 'aria-hidden', 'true' );
					gsap.set( outItem, { autoAlpha: 0, zIndex: 0 } );
					gsap.set( inItem, { zIndex: 1 } );

					current   = next;
					animating = false;
					if ( autoplay ) startAutoplay();
				},
			} );

			// --- Reduced motion path -------------------------------------------
			if ( REDUCED_MOTION ) {
				gsap.set( inItem, { zIndex: 2 } );
				inItem.setAttribute( 'aria-hidden', 'false' );
				tl
					.to( outItem, { autoAlpha: 0, duration: 0.3 }, 0 )
					.fromTo(
						inItem,
						{ autoAlpha: 0 },
						{ autoAlpha: 1, duration: 0.3 },
						0.15
					);
				return;
			}

			// --- Full animation path ------------------------------------------

			// Step 1: Exit the out-item's lines upward (forward) or downward (backward).
			if ( outLines.length ) {
				tl.to(
					outLines,
					{
						yPercent: direction === 1 ? -100 : 100,
						duration: 0.45,
						stagger: 0.035,  // Cascade from first to last line.
						ease: 'power3.in',
					},
					0 // Start at the beginning of the timeline.
				);
			} else {
				// Fallback for slides without SplitText (e.g. empty content).
				tl.to( outItem, { autoAlpha: 0, duration: 0.3 }, 0 );
			}

			// Step 2: Simultaneously exit the out-item's image by clipping from below.
			if ( outImg ) {
				tl.to(
					outImg,
					{ clipPath: 'inset(0 0 100% 0)', duration: 0.45, ease: 'power3.in' },
					0
				);
			}

			// Step 3 (at 0.4 s): Bring the incoming slide above the outgoing slide
			// and reset its line/image start positions before the reveal begins.
			tl.call(
				() => {
					gsap.set( inItem, { autoAlpha: 1, zIndex: 2 } );
					inItem.setAttribute( 'aria-hidden', 'false' );
					outItem.setAttribute( 'aria-hidden', 'true' );

					const enterY = direction === 1 ? 100 : -100;
					if ( inQuoteLines.length ) {
						gsap.set( inQuoteLines, { yPercent: enterY } );
					}
					if ( inMetaLines.length ) {
						gsap.set( inMetaLines, { yPercent: enterY } );
					}
					if ( inImg ) {
						gsap.set( inImg, { clipPath: 'inset(100% 0 0 0)' } );
					}
				},
				null,
				0.4
			);

			// Step 4: Reveal quote lines, then name/subtitle shortly after.
			if ( inQuoteLines.length ) {
				tl.to(
					inQuoteLines,
					{
						yPercent: 0,
						duration: 0.7,
						stagger: 0.06,
						ease: 'power3.out',
					},
					0.45
				);
			}

			if ( inMetaLines.length ) {
				tl.to(
					inMetaLines,
					{
						yPercent: 0,
						duration: 0.7,
						stagger: 0.06,
						ease: 'power3.out',
					},
					0.45 + META_REVEAL_DELAY
				);
			}

			if ( ! inQuoteLines.length && ! inMetaLines.length ) {
				// Fallback fade-in for slides without split lines.
				tl.fromTo(
					inItem,
					{ autoAlpha: 0 },
					{ autoAlpha: 1, duration: 0.4 },
					0.4
				);
			}

			// Step 4b: Reveal the in-item's image simultaneously with the lines.
			if ( inImg ) {
				tl.to(
					inImg,
					{ clipPath: 'inset(0% 0 0 0)', duration: 0.8, ease: 'power3.out' },
					0.45
				);
			}
		}

		// --- Navigation ---------------------------------------------------------

		/** Go to the previous slide (wraps around). */
		function prev() {
			stopAutoplay(); // User interaction resets the autoplay timer.
			goTo( ( current - 1 + items.length ) % items.length, -1 );
		}

		/** Go to the next slide (wraps around). */
		function next() {
			stopAutoplay();
			goTo( ( current + 1 ) % items.length, 1 );
		}

		if ( prevBtn ) prevBtn.addEventListener( 'click', prev );
		if ( nextBtn ) nextBtn.addEventListener( 'click', next );

		/**
		 * Keyboard navigation via arrow keys.
		 * Scoped to when the carousel is in the viewport (tracked by ScrollTrigger
		 * below) so that pressing ← → on a page with multiple carousels only
		 * controls the one currently visible, not all of them.
		 * Ignored when the user is actively typing in an input or contentEditable.
		 */
		document.addEventListener( 'keydown', ( e ) => {
			if ( ! inViewport ) return;

			const active   = document.activeElement;
			const isTyping =
				active &&
				( active.tagName === 'INPUT' ||
					active.tagName === 'TEXTAREA' ||
					active.isContentEditable );
			if ( isTyping ) return;

			if ( e.key === 'ArrowLeft' )  { e.preventDefault(); prev(); }
			if ( e.key === 'ArrowRight' ) { e.preventDefault(); next(); }
		} );

		// --- Autoplay + ScrollTrigger -------------------------------------------

		/**
		 * Start the autoplay countdown.
		 * Clears any existing timer first so calling this multiple times is safe.
		 * Uses setTimeout rather than setInterval so that each slide's pause
		 * duration can be adjusted without drift.
		 */
		function startAutoplay() {
			if ( ! autoplay || items.length < 2 ) return;
			stopAutoplay();
			autoTimer = setTimeout( () => {
				goTo( ( current + 1 ) % items.length, 1 );
			}, autoplayDuration );
		}

		/** Cancel any pending autoplay timer. */
		function stopAutoplay() {
			if ( autoTimer ) {
				clearTimeout( autoTimer );
				autoTimer = null;
			}
		}

		if ( typeof ScrollTrigger !== 'undefined' ) {
			gsap.registerPlugin( ScrollTrigger );

			/**
			 * Track whether the carousel is in the viewport.
			 * start: 'top bottom' fires when the top of the carousel enters the
			 * bottom of the viewport (i.e. it becomes partially visible).
			 * end: 'bottom top' fires when the bottom of the carousel exits the top
			 * of the viewport (i.e. it has fully scrolled past).
			 *
			 * onEnter / onEnterBack distinguish scrolling down vs scrolling up so
			 * that both directions are handled correctly.
			 */
			ScrollTrigger.create( {
				trigger: wrap,
				start: 'top bottom',
				end: 'bottom top',
				onEnter() {
					inViewport = true;
					if ( autoplay ) startAutoplay();
				},
				onEnterBack() {
					inViewport = true;
					if ( autoplay ) startAutoplay();
				},
				onLeave() {
					inViewport = false;
					stopAutoplay(); // Pause when scrolled out of view.
				},
				onLeaveBack() {
					inViewport = false;
					stopAutoplay();
				},
			} );
		} else {
			// ScrollTrigger failed to load (e.g. CDN blocked). Fall back to treating
			// the carousel as always-in-viewport so autoplay still works.
			inViewport = true;
			if ( autoplay ) startAutoplay();
		}

		// Initialise the counter display to "1" (render.php writes "1" too, but
		// this ensures it stays correct even if the DOM was pre-rendered differently).
		if ( counterEl ) counterEl.textContent = '1';
	}

	// -------------------------------------------------------------------------
	// Boot — find every carousel on the page and initialise it independently.
	// -------------------------------------------------------------------------
	document
		.querySelectorAll( '[data-testimonial-wrap]' )
		.forEach( initCarousel );

} )();
